import { GRAPHQL_TRACE_KEY } from "@digits-shared/components/Contexts/GraphQLTracerContext"
import { DIGITS_EMPLOYEE_OVERRIDE } from "@digits-shared/session/Session"

/**
 * simpleEmployeeFeatures follow a standardized configuration relying on solely the name, description, and employee/user key mapping.
 * Adding a value to this list will enable the feature to be toggled on/off via the internal Employees Features page without additional configuration.
 * Optionally provide a boolean value for `requiresReload` to control the reload behavior.
 * If omitted, the page will not be reloaded when the feature is toggled
 */
const simpleEmployeeFeatures = [
  {
    name: "Grid Overlay",
    description: "Displays an overlay of the design system grid.",
    employeeFeatureKey: "gridOverlayEnabled",
    userPreferenceKey: "gdt",
  },
  {
    name: "Disable Graphql Batching",
    description: "Each operation will be single request",
    employeeFeatureKey: "graphqlNoBatching",
    userPreferenceKey: "graphql-no-batching",
    // this feature is checked in setupApolloClient,
    // so without a reload it would only be updated on legal entity change
    requiresReload: true,
  },
  {
    name: "Export transaction detail Enabled",
    description: "Enable exporting entries from the transaction detail view",
    employeeFeatureKey: "exportTransactionDetailEnabled",
    userPreferenceKey: "export-transaction-detail-enabled",
  },
  {
    name: "Organization Dashboard",
    description: "Enables the Organization Dashboard",
    employeeFeatureKey: "orgDashboardEnabled",
    userPreferenceKey: "org-dashboard-enabled",
  },
  {
    name: "Products & Services Catalog",
    description: "Enables the Products & Services catalog tab.",
    employeeFeatureKey: "productCatalogEnabled",
    userPreferenceKey: "product-catalog-enabled",
  },
  {
    name: "Quality Control Action Items",
    description: "Includes Quality Control action items in action-item list and summary queries",
    employeeFeatureKey: "qualityControlActionItemsEnabled",
    userPreferenceKey: "quality-control-action-items-enabled",
  },
  {
    name: "Agent Policy",
    description: "Enables access to the agent policy editor",
    employeeFeatureKey: "agentPolicyEnabled",
    userPreferenceKey: "agent-policy-enabled",
  },
  {
    name: "Close Timeline",
    description: "Enables the desktop close timeline in the period selector slot",
    employeeFeatureKey: "closeTimelineEnabled",
    userPreferenceKey: "close-timeline-enabled",
  },
  {
    name: "Firm Dashboard List View",
    description: "Enables the list view layout for the Firm Dashboard.",
    employeeFeatureKey: "firmDashboardListViewEnabled",
    userPreferenceKey: "firm-dashboard-list-view-enabled",
  },
] as const satisfies Array<EmployeeFeatureConfig>

/**
 * These features require a non-standardized configuration and must be manually configured within DigitsEmployeeFeatures.tsx
 */
const complexEmployeeFeatures = {
  digitsEmployeeOverride: DIGITS_EMPLOYEE_OVERRIDE,
  graphqlTracing: GRAPHQL_TRACE_KEY,
} as const satisfies Record<string, string>

export const SIMPLE_EMPLOYEE_FEATURES = simpleEmployeeFeatures as OptionalRequiresReload<
  typeof simpleEmployeeFeatures
>

/**
 * key-value mapping of named internal employee feature keys to session user preference keys
 * The values of all features in this list can be accessed directly via the hook `useReadEmployeeFeature(employeeFeatureKey)`
 */
export const employeeFeatureKeys = {
  ...complexEmployeeFeatures,
  ...SIMPLE_EMPLOYEE_FEATURES.reduce(
    (acc, f) => ({ ...acc, [f.employeeFeatureKey]: f.userPreferenceKey }),
    {} as {
      [K in (typeof SIMPLE_EMPLOYEE_FEATURES)[number] as K["employeeFeatureKey"]]: K["userPreferenceKey"]
    }
  ),
} as const

export type EmployeeFeatureConfig = {
  name: string
  description: string
  employeeFeatureKey: string
  userPreferenceKey: string
  requiresReload?: boolean
}

export type EmployeeFeatureKey = keyof typeof employeeFeatureKeys

export type EmployeeFeatureUserPreferenceKey = (typeof employeeFeatureKeys)[EmployeeFeatureKey]

type OptionalRequiresReload<T extends readonly unknown[]> = {
  [K in keyof T]: T[K] extends { requiresReload: boolean }
    ? T[K]
    : T[K] & { requiresReload: undefined }
}
